"use client";

import { useEffect, useId, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { DECK_N, TAG, drawTrade, makeTrade } from "./tradeDeck";

/* The four process "stages" (Figma palcos, 520 × 404 each; 1rem = px / 8). Built from the Figma layers; motion
   ported from the prototype: 01 the trade deck being scanned, 02 the crowd being filtered down to the top 1 %,
   03 the live interview (audio wave + REC), 04 the strategy models cycling. Reduced motion = the Figma still. */

const r = (px: number) => `${px / 8}rem`;
const A = "/quarterly-event/process";

function useInView<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setOn(e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, on] as const;
}

const reduced = () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function Grain({ size = 137.143, opacity, className }: { size?: number; opacity?: number; className?: string }) {
  return <i aria-hidden="true" className={cn("pointer-events-none absolute top-0 left-0 h-[50.25rem] w-[65rem] bg-repeat", className)} style={{ backgroundImage: `url(${A}/p1/n-c46b9.png)`, backgroundSize: `${r(size)} ${r(size)}`, opacity }} />;
}

/* ── 01 · the data: trade deck (prototype 26/10; mobile Figma 12405:33048 at 240 tall) ────────────────────
   Three trade cards stacked: the scanner reads the front one (win, loss or blown account), it drops out, the next one
   takes over and the trader count climbs to 20,000+. Engine in `tradeDeck`. Reduced motion = the three charts drawn. */
const DECK0 = [0, 1, 2].map(makeTrade);
const CHIP =
  "mb-0 flex h-[3rem] items-center gap-[0.75rem] rounded-[0.5rem] border border-solid border-white/10 bg-white/[0.04] px-[1rem] font-geist text-[1.75rem] leading-[2.5rem] font-medium whitespace-nowrap text-white/75 lg:h-[4rem] lg:gap-[1rem] lg:px-[1.5rem]";

export function QeStageScanner() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const C = [...root.querySelectorAll<HTMLElement>("[data-card]")];
    const cnt = root.querySelector<HTMLElement>("[data-count]"), pg = root.querySelector<HTMLElement>("[data-progress]");
    const cv = (c: HTMLElement) => c.querySelector("canvas")!;
    const tag = (c: HTMLElement) => c.querySelector<HTMLElement>("[data-tag]")!;
    const S = DECK0.slice();
    let k = 1, font = "sans-serif";
    const still = () => {
      k = parseFloat(getComputedStyle(document.documentElement).fontSize) / 8 || 1;
      font = getComputedStyle(cv(C[0])).fontFamily;
      C.forEach((c, i) => c.dataset.p !== "x" && drawTrade(cv(c), S[i], DECK_N, 0, false, k, font));
    };
    still();
    document.fonts?.ready.then(still);
    window.addEventListener("resize", still);
    if (reduced()) return () => window.removeEventListener("resize", still);

    let vis = false;
    const io = new IntersectionObserver(([e]) => { vis = e.isIntersecting; });
    io.observe(root);
    const SCAN = 4200, HOLD = 1300, CYC = SCAN + HOLD + 900;
    let t0: number | null = null, paused: number | null = null, f = 0, total = 0, swapped = false, raf = 0;
    const loop = (now: number) => {
      raf = requestAnimationFrame(loop);
      // pause off screen instead of restarting, so a half-swapped deck never gets stuck
      if (!vis) { paused ??= now; return; }
      if (paused !== null && t0 !== null) t0 += now - paused;
      paused = null;
      t0 ??= now;
      const T = now - t0, front = C.find((c) => c.dataset.p === (swapped ? "x" : "0"));
      if (!front) return;
      const Sf = S[C.indexOf(front)], si = Math.min(DECK_N + 0.6, (T / SCAN) * (DECK_N + 0.6));
      drawTrade(cv(front), Sf, si, now, true, k, font);
      tag(front).style.opacity = si > (Sf.t === "b" ? Sf.hit + 0.8 : Sf.TB + 0.6) ? "1" : "0";
      if (pg) pg.style.width = `${(Math.min(1, T / SCAN) * 100).toFixed(2)}%`;
      if (cnt) {
        const v = Math.min(20000, Math.round(((total + Math.min(1, T / SCAN)) * 2650) / 50) * 50);
        cnt.textContent = v >= 20000 ? "20,000+" : v.toLocaleString("en-US");
      }
      if (T > SCAN + HOLD && !swapped) {
        swapped = true;
        front.dataset.p = "x";
        C.forEach((c) => { if (c !== front) c.dataset.p = String(Number(c.dataset.p) - 1); });
        const nf = C.find((c) => c.dataset.p === "0")!;
        drawTrade(cv(nf), S[C.indexOf(nf)], 0, now, true, k, font);
        tag(nf).style.opacity = "0";
      }
      if (T >= CYC) {
        f++;
        total++;
        const old = C.find((c) => c.dataset.p === "x")!, i = C.indexOf(old);
        S[i] = makeTrade(f + 2);
        old.querySelector("[data-label]")!.textContent = S[i].sym;
        tag(old).dataset.t = S[i].t;
        tag(old).textContent = TAG[S[i].t];
        tag(old).style.opacity = "1";
        old.classList.add("qe-nt");
        old.dataset.p = "2";
        drawTrade(cv(old), S[i], DECK_N, now, false, k, font);
        void old.offsetWidth;
        old.classList.remove("qe-nt");
        swapped = false;
        t0 = now;
      }
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("resize", still);
    };
  }, []);

  return (
    <div ref={ref} className="qe-s1 relative flex h-[30rem] w-[43.625rem] flex-col gap-[1rem] overflow-hidden bg-gradient-to-b from-[#131413] to-[#121f17] p-[1.5rem] lg:h-[50.5rem] lg:w-[65rem] lg:gap-[1.5rem] lg:p-[3rem]">
      <div className="relative flex items-center justify-between">
        <p className={CHIP}>Every trade</p>
        <p className={CHIP}>
          <i className="qe-blink block h-[0.75rem] w-[0.75rem] rounded-full bg-[#9ee3b0]" />
          <span>
            <span data-count className="inline-block min-w-[4.4ch] text-right tabular-nums">20,000+</span>&nbsp;traders
          </span>
        </p>
      </div>
      <div className="relative flex-1">
        {DECK0.map((s, i) => (
          <div key={i} data-card data-p={i} className="qe-s1c absolute inset-x-0 top-[2.5rem] bottom-0 overflow-hidden rounded-[0.5rem] border border-solid border-white/10 bg-[#151816] lg:top-[3.5rem]">
            <div className="qe-s1h absolute inset-x-0 top-0 flex h-[4rem] items-center justify-between border-0 border-b border-solid border-white/[0.06] px-[1.25rem] font-geist text-[1.75rem] leading-[2.5rem] font-medium text-white/[0.55] lg:h-[5.5rem] lg:px-[1.5rem]">
              <span data-label>{s.sym}</span>
              <span data-tag data-t={s.t} className="qe-s1t flex h-[2.75rem] items-center rounded-[0.375rem] border border-solid px-[0.75rem] lg:h-[3rem] lg:px-[1rem]">{TAG[s.t]}</span>
            </div>
            <canvas aria-hidden="true" className="absolute inset-x-0 top-[4rem] bottom-0 block h-[calc(100%-4rem)] w-full font-geist lg:top-[5.5rem] lg:h-[calc(100%-5.5rem)]" />
          </div>
        ))}
      </div>
      <i className="relative block h-[0.25rem] shrink-0 overflow-hidden rounded-[0.125rem] bg-white/[0.08]">
        <i data-progress className="block h-full w-full bg-[#9ee3b0]" />
      </i>
      <Grain size={128} />
    </div>
  );
}

/* ── 02 · the ranking: crowd in perspective (Figma 12568:1784 — the four "passo 02" frames, 26/10) ─────────
   1 · the crowd appears as a wave from the centre outward; 2 · all visible, the ranking lights up; 3 · 99 % go
   out; 4 · the top 1 % jumps and stands out (rings, label, the neighbours step aside). Nine rows, every icon at its
   Figma position, each row carried on in its rhythm past the right edge (Figma's stops short of it); the phase-4
   offsets are Figma's too. Rest / reduced motion = frame 4. */
const CX = 306, CY = 195;
const seq = (x0: number, dx: number, n: number) => Array.from({ length: n }, (_, i) => x0 + i * dx);
// y, icon size, fill opacity, blur and the rest x of each row; `m` = phase-4 [x, dy] of the icons that step aside
const ROWS: { y: number; s: number; o: number; b: number; xs: number[]; m?: Record<number, [number, number]> }[] = [
  { y: 14, s: 11, o: 0.1, b: 2.4, xs: seq(-19, 18, 30) },
  { y: 29, s: 12, o: 0.15, b: 1.6, xs: seq(-23, 19, 29), m: { 243: [242.66, -0.57], 262: [261.53, -1.2], 281: [280.68, -1.61], 300: [300, -1.75], 319: [319.32, -1.61], 338: [338.47, -1.2], 357: [357.34, -0.57] } },
  { y: 57, s: 14, o: 0.22, b: 0.8, xs: seq(-39, 23, 25), m: { 191: [190.35, -0.47], 214: [212.01, -1.84], 237: [234.5, -3.17], 260: [257.88, -4.28], 283: [281.99, -4.98], 306: [306.45, -5.1], 329: [330.76, -4.61], 352: [354.45, -3.64], 375: [377.3, -2.37], 398: [399.26, -1] } },
  { y: 92, s: 16, o: 0.28, b: 0, xs: seq(-8, 26, 21), m: { 174: [171.1, -1.33], 200: [194.63, -3.12], 226: [219.45, -5.18], 252: [246.12, -7.29], 278: [274.88, -8.89], 304: [304.98, -9.29], 330: [334.64, -8.26], 356: [362.45, -6.34], 382: [388.2, -4.21], 408: [412.36, -2.26], 434: [435.45, -0.61] } },
  { y: 135, s: 19, o: 0.35, b: 0, xs: seq(-37, 31, 18), m: { 149: [145.49, -0.72], 180: [171.44, -2.23], 211: [198.34, -4.49], 242: [227.65, -7.98], 273: [263.22, -12.61], 304: [307.54, -14.29], 335: [348.13, -10.34], 366: [380, -6.1], 397: [407.85, -3.27], 428: [434.19, -1.43], 459: [459.9, -0.17] } },
  { y: 184, s: 21, o: 0.42, b: 0, xs: seq(-11, 34, 16).filter((x) => x !== 295), m: { 125: [124.13, 0], 159: [151.52, -0.02], 193: [178.91, -0.04], 227: [206.31, -0.09], 261: [233.71, -0.24], 329: [356.49, -0.25], 363: [383.88, -0.09], 397: [411.28, -0.04], 431: [438.67, -0.02], 465: [466.07, 0] } },
  { y: 236, s: 24, o: 0.49, b: 0, xs: seq(-64, 39, 15), m: { 131: [130.33, 0.13], 170: [162.83, 1.84], 209: [196.66, 4.62], 248: [234.65, 9.23], 287: [283.91, 14.05], 326: [337.36, 11.29], 365: [378.45, 6.02], 404: [413.26, 2.68], 443: [446.08, 0.66] } },
  { y: 294, s: 27, o: 0.57, b: 0, xs: seq(-35, 44, 13), m: { 185: [182.4, 1.63], 229: [224.62, 4.65], 273: [270.98, 6.99], 317: [319.47, 6.82], 361: [365.37, 4.31], 405: [407.18, 1.31] } },
  { y: 355, s: 30, o: 0.62, b: 2, xs: seq(-39, 49, 12) },
];
const CROWD = ROWS.map(({ y, s, o, xs, m }) =>
  xs.map((x) => {
    const [x4, dy] = m?.[x] ?? [x, 0];
    return { x, y, s, o, px: x4 - x, py: dy, d: Math.hypot(x + s / 2 - CX, y + s / 2 - CY) };
  }),
);
const PERSON = [
  "M3 15H13V12.5C12.9989 11.5721 12.6298 10.6825 11.9737 10.0263C11.3175 9.37018 10.4279 9.00109 9.5 9H6.5C5.57208 9.00109 4.68247 9.37018 4.02632 10.0263C3.37018 10.6825 3.00109 11.5721 3 12.5V15Z",
  "M4.5 4.5C4.5 5.19223 4.70527 5.86892 5.08986 6.4445C5.47444 7.02007 6.02107 7.46867 6.66061 7.73358C7.30015 7.99849 8.00388 8.0678 8.68282 7.93275C9.36175 7.7977 9.98539 7.46436 10.4749 6.97487C10.9644 6.48539 11.2977 5.86175 11.4327 5.18282C11.5678 4.50388 11.4985 3.80015 11.2336 3.16061C10.9687 2.52107 10.5201 1.97444 9.9445 1.58986C9.36892 1.20527 8.69223 1 8 1C7.07174 1 6.1815 1.36875 5.52513 2.02513C4.86875 2.6815 4.5 3.57174 4.5 4.5Z",
];
const MINT = "#9ee3b0";
const cl = (x: number) => Math.max(0, Math.min(1, x));
const eo = (x: number) => 1 - Math.pow(1 - cl(x), 3);
const eio = (x: number) => { x = cl(x); return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; };
const spr = (x: number) => { x = cl(x); return x >= 1 ? 1 : 1 - Math.cos(x * Math.PI * 2.4) * Math.exp(-5 * x); };
// the beats (ms): wave front at the far corner → everything lit → 99 % go out → the jump → fade out → again
const FRONT = 1000, DIM = 2600, JMP = 3800, OUT = 8000, CYC = 8800, FAR = 365;

export function QeStageCrowd() {
  const ref = useRef<HTMLDivElement>(null);
  const u = `qe-u${useId().replace(/:/g, "")}`;
  useEffect(() => {
    const svg = ref.current?.querySelector("svg");
    if (!svg || reduced()) return;
    const P = CROWD.flat();
    const D = [...svg.querySelectorAll<SVGUseElement>("[data-person]")].map((e, i) => ({
      e, p: P[i], a: Math.min(1, P[i].d / FAR) * FRONT + Math.random() * 60, o: Math.random() * 300,
    }));
    const sg = svg.querySelector<SVGGElement>("[data-top]")!, ic = sg.querySelector("use")!, tg = svg.querySelector<SVGGElement>("[data-tag]")!;
    const [pr, r2, r1] = [...sg.querySelectorAll("circle")];
    let vis = false, t0: number | null = null, raf = 0;
    const io = new IntersectionObserver(([e]) => { vis = e.isIntersecting; });
    io.observe(svg);
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (!vis) { t0 = null; return; }
      t0 ??= now;
      const T = (now - t0) % CYC, out = eo((T - OUT) / 700);
      D.forEach(({ e, p, a, o }) => {
        const ap = eo((T - a) / 350), dm = eio((T - DIM - o) / 500), ps = eo((T - JMP + 60) / 600);
        e.style.opacity = (p.o * ap * (1 - 0.82 * dm) * (1 - out)).toFixed(3);
        e.style.transform = `translate(${(p.px * ps).toFixed(2)}px,${((1 - ap) * 6 + dm * 2 + p.py * ps).toFixed(2)}px)`;
      });
      // the survivor: first to appear (24.8, white 55 %), then the jump to 40 in mint
      const ap0 = eo((T - 80) / 350), j = cl((T - JMP) / 900), s = 0.62 + 0.38 * spr(j), hop = Math.sin(cl((T - JMP) / 560) * Math.PI) * -16;
      const ring = eo((T - JMP - 120) / 700), r2k = eo((T - JMP - 320) / 800), lab = eo((T - JMP - 500) / 600);
      sg.setAttribute("transform", `translate(${CX} ${(CY + hop + (1 - ap0) * 6).toFixed(2)}) scale(${s.toFixed(3)})`);
      sg.style.opacity = (ap0 * (1 - out)).toFixed(3);
      ic.setAttribute("fill", j > 0.08 ? MINT : "#fff");
      ic.style.opacity = j > 0.08 ? "1" : "0.55";
      r1.setAttribute("fill-opacity", (ring * 0.06).toFixed(3));
      r1.setAttribute("stroke-opacity", (ring * 0.45).toFixed(3));
      r2.setAttribute("stroke-opacity", (r2k * 0.14).toFixed(3));
      r2.setAttribute("r", (36 + 16 * r2k).toFixed(1));
      const pp = T > JMP + 1200 && T < OUT ? ((T - JMP - 1200) % 1700) / 1700 : 0;
      pr.setAttribute("r", (34 + 30 * pp).toFixed(1));
      pr.setAttribute("stroke-opacity", pp ? ((1 - pp) * 0.45).toFixed(3) : "0");
      tg.setAttribute("opacity", (lab * (1 - out)).toFixed(3));
      tg.setAttribute("transform", `translate(0 ${((1 - lab) * 8).toFixed(2)})`);
      tg.style.filter = `blur(${((1 - lab) * 4).toFixed(2)}px)`;
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
    };
  }, []);

  return (
    <div ref={ref} className="relative h-[50.5rem] w-[65rem] overflow-hidden bg-[linear-gradient(180deg,#151716,#0a0a0a)]">
      <svg viewBox="0 0 520 404" aria-hidden="true" className="absolute top-0 left-0 h-[50.5rem] w-[65rem] overflow-visible">
        <defs>
          <symbol id={u} viewBox="0 0 16 16">{PERSON.map((d) => <path key={d} d={d} />)}</symbol>
        </defs>
        {CROWD.map((row, r) => (
          <g key={r} fill="#fff" style={{ filter: ROWS[r].b ? `blur(${ROWS[r].b}px)` : undefined }}>
            {row.map((p) => (
              <use
                key={p.x}
                data-person
                href={`#${u}`}
                x={p.x}
                y={p.y}
                width={p.s}
                height={p.s}
                style={{ opacity: p.o * 0.18, transform: `translate(${p.px.toFixed(2)}px,${(2 + p.py).toFixed(2)}px)` }}
              />
            ))}
          </g>
        ))}
        <g data-top transform={`translate(${CX} ${CY})`}>
          <circle r={34} fill="none" stroke={MINT} strokeOpacity={0} />
          <circle r={52} fill="none" stroke={MINT} strokeOpacity={0.14} />
          <circle r={34} fill={MINT} fillOpacity={0.06} stroke={MINT} strokeOpacity={0.45} />
          <use href={`#${u}`} x={-20} y={-20} width={40} height={40} fill={MINT} />
        </g>
        <g data-tag>
          <line x1={CX} y1={127} x2={CX} y2={143} stroke={MINT} strokeOpacity={0.5} />
          <rect x={273.5} y={97.5} width={65} height={29} rx={4} fill="#fff" fillOpacity={0.08} stroke="#fff" strokeOpacity={0.12} />
          <text x={CX} y={117} textAnchor="middle" fill="#fff" fontSize={14} fontWeight={500} className="font-geist">Top 1%</text>
        </g>
      </svg>
      <Grain />
    </div>
  );
}

/* ── 03 · the interviews: tablet (Figma 12375:9762 palco) ───────────────────── */
const WAVE = [8, 16, 27, 13, 32, 19, 11, 24, 35, 16, 8, 21, 30, 30, 30, 30, 30, 30, 30, 13, 8];

/** `compact`: the thank-you card look (Figma 12405:4947) — dark stage and phone type sizes on desktop too. */
export function QeStageInterview({ compact = false }: { compact?: boolean }) {
  const [ref, on] = useInView<HTMLDivElement>();
  const [h, setH] = useState(WAVE);
  useEffect(() => {
    if (!on || reduced()) { setH(WAVE); return; }
    let k = 0;
    const id = window.setInterval(() => {
      k++;
      setH(WAVE.map((v, i) => Math.max(4, Math.min(36, Math.round(v * (0.45 + 0.75 * Math.abs(Math.sin(k * 0.9 + i * 1.7))))))));
    }, 140);
    return () => window.clearInterval(id);
  }, [on]);

  return (
    <div ref={ref} className={cn("relative h-[50.5rem] w-[65rem] overflow-hidden bg-gradient-to-b from-[#141615] to-[#122119]", !compact && "lg:from-[#ece9e5] lg:to-[#d8d3cc]")}>
      <div className="absolute top-[6.375rem] left-[6.375rem] h-[51.35rem] w-[65rem] overflow-hidden rounded-[3.95rem] border-solid border-white/[0.16] bg-[#050505]" style={{ borderWidth: r(1.58) }}>
        <div className="absolute top-[0.9875rem] left-[0.9875rem] h-[48.98rem] w-[62.625rem] overflow-hidden rounded-[2.765rem] bg-[#111]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={`${A}/p3/n-a2165.png`} alt="" className="qe-iv-ph absolute max-w-none object-cover blur-[0.196rem]" style={{ left: r(100.52), top: r(8.52), width: r(439.229), height: r(472.694) }} />
          <i className="absolute top-0 left-0 h-[48.98rem] w-[52.93rem] bg-[linear-gradient(90deg,#111_25%,rgba(17,17,17,0)_60%)]" />
          <div className="absolute flex flex-col items-start justify-between" style={{ left: r(23.52), top: r(22.52), width: r(151), height: r(298) }}>
            <div className="flex items-center gap-[0.875rem]">
              {["1:1 interview", "Rec"].map((t, i) => (
                <p key={t} className={cn("mb-0 flex items-center gap-[0.79rem] rounded-[0.395rem] border-solid border-white/[0.14] bg-black/40 font-geist text-[2.446rem] leading-[3.495rem] font-medium whitespace-nowrap text-white backdrop-blur-[0.5925rem]", compact ? "lg:text-[2.097rem]" : "lg:text-[1.3825rem]", "lg:leading-[1.975rem]")} style={{ borderWidth: r(0.79), padding: `${r(4.74)} ${r(7.9)}` }}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  {i === 1 && <img src={`${A}/p3/n-5c69e.svg`} alt="" className="qe-rec max-w-none" style={{ width: r(6.32), height: r(6.32) }} />}
                  {t}
                </p>
              ))}
            </div>
            <div className="flex flex-col items-center gap-[1.5rem]">
              {/* compact desktop (12405:4947): the name keeps its full 18.085 px line box, 6 px over the subtitle */}
              <div className={cn("flex w-full flex-col items-start gap-[1rem]", compact && "lg:gap-[1.0488rem]")}>
                <p className={cn("mb-0 h-[2.125rem] font-helvetica-lt text-[3.145rem] font-medium tracking-[-0.03em] whitespace-nowrap text-white", compact ? "lg:h-auto" : "lg:text-[2.37rem]")} style={{ lineHeight: r(25.28) }}>Order Flow Expert</p>
                <p className={cn("mb-0 w-full font-geist text-[2.446rem] font-normal whitespace-nowrap text-white/60", !compact && "lg:text-[1.3825rem]")} style={{ lineHeight: r(15.8) }}>Never blew an account</p>
              </div>
              <div className="flex h-[7rem] w-[27.375rem] items-center rounded-[0.395rem] bg-[#f6f6f6] backdrop-blur-[1.185rem]" style={{ gap: r(12.64), padding: `${r(9.48)} ${r(15.8)}` }}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={`${A}/p3/n-9ceda.svg`} alt="" className="max-w-none shrink-0" style={{ width: r(15.8), height: r(15.8) }} />
                <div className="flex h-[4.5rem] min-w-px flex-1 items-center justify-between">
                  {h.map((v, i) => <i key={i} className="block w-[0.25rem] shrink-0 rounded-[0.099rem] bg-[#296e5a] transition-[height] duration-150 ease-out" style={{ height: r(v) }} />)}
                </div>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={`${A}/p3/n-46e02.svg`} alt="" className="max-w-none shrink-0" style={{ width: r(15.8), height: r(15.8) }} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Grain className={compact ? "opacity-50" : "opacity-50 lg:opacity-100"} />
    </div>
  );
}

/* ── 04 · the strategies: model slider (Figma 12375:9906 palco) ───────────── */
const MODELS = [
  { t: "The exit model", g: `${A}/p4/n-88d27.svg` },
  { t: "The entry model", g: `${A}/p4/n-00745.svg` },
  { t: "The risk process", g: `${A}/p4/n-3871f.svg` },
];
type Model = (typeof MODELS)[number];

/* Figma slots in the 76.615 × 28.835 row: sides 14.8125 × 27.255 (centred, 1.375 gap), centre 44.24 × 28.835.
   ±2 sit just off stage: the card that leaves slides out to −2, the next one slides in from +2.
   [left, top, width, height] in rem. */
const SLOTS: Record<number, number[]> = {
  [-2]: [-16.1875, 0.79, 14.8125, 27.255],
  [-1]: [0, 0.79, 14.8125, 27.255],
  [0]: [16.1875, 0, 44.24, 28.835],
  [1]: [61.8025, 0.79, 14.8125, 27.255],
  [2]: [77.99, 0.79, 14.8125, 27.255],
};
const SLIDE = "qe-sl"; // timing lives in QE_STAGE_CSS
const rem = (v: number) => `${v}rem`;
/** Figma outline SVGs: the outgoing one drops at once, the incoming one settles in at the end of the slide. */
const swap = (on: boolean) => ({ opacity: on ? 1 : 0, transitionDuration: "150ms", transitionDelay: on ? "650ms" : "0ms" });
/* Prototype 26/10: side screens sit back blurred and dimmed, the centre one sharpens as it arrives; model names stay
   blurred everywhere (locked until the premiere). */
const DEPTH = ["blur(0rem) brightness(1)", "blur(0.5rem) brightness(.78)", "blur(1rem) brightness(.78)"];

/* While a card slides, an opaque #111 fill morphs between the same outlines (Figma paths sampled into % polygons of
   the slot), so the card never thins out between the two SVGs. Hidden at rest, where the SVGs alone draw the still. */
type Pt = [number, number];
type Curve = [Pt, Pt, Pt, Pt];
const bez = ([a, b, c, d]: Curve, n = 10): Pt[] =>
  Array.from({ length: n + 1 }, (_, k) => {
    const t = k / n, u = 1 - t;
    return [0, 1].map((j) => u * u * u * a[j] + 3 * u * u * t * b[j] + 3 * u * t * t * c[j] + t * t * t * d[j]) as Pt;
  });
/** Top curve + bottom curve of a Figma path (viewBox `vw × vh`, drawn in the % box `x y w h` of the slot) → polygon. */
const outline = (top: Curve, bottom: Curve, [vw, vh]: Pt, [x, y, w, h]: number[]) =>
  `polygon(${[...bez(top), ...bez(bottom)].map(([px, py]) => `${(x + (px / vw) * w).toFixed(3)}% ${(y + (py / vh) * h).toFixed(3)}%`).join(",")})`;
const SIDE_BOX = [-0.3308, 7.2464, 100.6582, 93.1572];
const OUT_MID = outline([[0.395, 0.413], [118.368, 5.68], [236.342, 5.68], [354.315, 0.413]], [[354.315, 231.093], [236.342, 225.826], [118.368, 225.826], [0.395, 231.093]], [354.71, 231.506], [-0.11, -0.18, 100.22, 100.36]);
const OUT_L = outline([[0.395, 0.449], [39.895, 5.505], [79.395, 11.825], [118.895, 19.409]], [[118.895, 183.729], [79.395, 191.313], [39.895, 197.633], [0.395, 202.689]], [119.29, 203.138], SIDE_BOX);
const OUT_R = outline([[0.395, 19.409], [39.895, 11.825], [79.395, 5.505], [118.895, 0.449]], [[118.895, 202.689], [79.395, 197.633], [39.895, 191.313], [0.395, 183.729]], [119.29, 203.138], SIDE_BOX);
// Side outline (14.91 × 25.39 at 1.975 / −0.049 in the 14.8125 × 27.255 slot) as % so it stretches with the card.
const SIDE_SHAPE = "absolute top-[7.2464%] left-[-0.3308%] h-[93.1572%] w-[100.6582%] max-w-none";
const MID_SHAPE = "absolute inset-[-0.18%_-0.11%] h-[100.36%] w-[100.22%] max-w-none";

/** One card; every layer moves between the centre and side layouts of the Figma still. */
function ModelCard({ mod, slot, moving }: { mod: Model; slot: number; moving: boolean }) {
  const mid = slot === 0;
  const [x, y, w, h] = SLOTS[slot];
  /* eslint-disable @next/next/no-img-element */
  return (
    <div
      data-slot={slot}
      className={cn("absolute", SLIDE, Math.abs(slot) === 1 && "cursor-pointer")}
      style={{ left: rem(x), top: rem(y), width: rem(w), height: rem(h), zIndex: mid ? 1 : 0, filter: DEPTH[Math.abs(slot)], opacity: Math.abs(slot) === 1 ? 0.9 : 1 }}
    >
      <i className={cn("absolute inset-0 bg-[#111]", SLIDE)} style={{ clipPath: mid ? OUT_MID : slot < 0 ? OUT_L : OUT_R, transitionProperty: "clip-path", opacity: moving ? 1 : 0 }} />
      <img src={`${A}/p4/n-aa3a9.svg`} alt="" className={cn(MID_SHAPE, SLIDE)} style={swap(mid)} />
      <img src={`${A}/p4/n-6e865.svg`} alt="" className={cn(SIDE_SHAPE, SLIDE)} style={swap(slot < 0)} />
      <img src={`${A}/p4/n-7e539.svg`} alt="" className={cn(SIDE_SHAPE, SLIDE)} style={swap(slot > 0)} />
      <div className={cn("absolute overflow-hidden", SLIDE)} style={mid ? { left: rem(0.055), top: rem(0.025), width: rem(44.125), height: rem(28.75) } : { left: 0, top: 0, width: rem(14.8125), height: rem(25.675) }}>
        <p className={cn("absolute mb-0 font-geist text-[2.7956rem] leading-[4.194rem] font-medium tracking-[-0.03em] whitespace-nowrap text-white blur-[0.75rem] select-none lg:text-[2.5rem] lg:leading-[normal]", SLIDE)} style={mid ? { left: rem(2.315), top: rem(2.74) } : { left: rem(1.58), top: rem(5.53) }}>{mod.t}</p>
        <img src={mod.g} alt="" className={cn("absolute max-w-none", SLIDE)} style={mid ? { left: rem(2.216), top: rem(6.69), width: rem(40.135), height: rem(16.195) } : { left: rem(1.58), top: rem(9.085), width: rem(11.6525), height: rem(10.8625) }} />
        <div className={cn("absolute top-[23.75rem] left-[2.315rem] flex items-center lg:top-[24.5rem]", SLIDE)} style={{ gap: r(6.32), opacity: mid ? 1 : 0, transitionDuration: mid ? "400ms" : "200ms", transitionDelay: mid ? "450ms" : "0ms" }}>
          <img src={`${A}/p4/n-81bb6.svg`} alt="" className="max-w-none" style={{ width: r(12.64), height: r(12.64) }} />
          <p className="mb-0 font-geist text-[2.446rem] leading-[3.495rem] font-normal whitespace-nowrap text-white/60 lg:text-[1.3825rem] lg:leading-[1.975rem]">Locked until Nov 24</p>
        </div>
      </div>
      <img src={`${A}/p4/n-f4f5c.svg`} alt="" className={cn(MID_SHAPE, SLIDE)} style={swap(mid)} />
      <img src={`${A}/p4/n-add9e.svg`} alt="" className={cn(SIDE_SHAPE, SLIDE)} style={swap(slot < 0)} />
      <img src={`${A}/p4/n-7d46c.svg`} alt="" className={cn(SIDE_SHAPE, SLIDE)} style={swap(slot > 0)} />
    </div>
  );
  /* eslint-enable @next/next/no-img-element */
}

/** `compact`: the thank-you card look (Figma 12405:1965) — the phone #eee stage on desktop too.
    Slides every 3.6 s while in view (paused under the mouse); swipe, or tap a side card / dot, to move it by hand. */
export function QeStageModels({ compact = false }: { compact?: boolean }) {
  const [ref, on] = useInView<HTMLDivElement>();
  const [c, setC] = useState(1);
  const [hold, setHold] = useState(false);
  const [moving, setMoving] = useState(false);
  const down = useRef<number | null>(null);
  const first = useRef(true);
  useEffect(() => {
    if (!on || hold || reduced()) return;
    const id = window.setTimeout(() => setC((v) => v + 1), 3600);
    return () => window.clearTimeout(id);
  }, [on, hold, c]);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    setMoving(true);
    const id = window.setTimeout(() => setMoving(false), 850);
    return () => window.clearTimeout(id);
  }, [c]);
  const go = (d: number) => d && setC((v) => v + d);
  const pg = (((c - 1) % 3) + 3) % 3;

  return (
    <div ref={ref} className={cn("relative h-[50.5rem] w-[65rem] overflow-hidden bg-[#eee]", !compact && "lg:bg-[#dededc]")} onPointerEnter={(e) => e.pointerType === "mouse" && setHold(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setHold(false)}>
      <div className="absolute top-[10.875rem] left-[calc(50%+0.0575rem)] flex w-[76.615rem] -translate-x-1/2 flex-col items-center gap-[1.75rem]">
        <div
          className="relative h-[28.835rem] w-full touch-pan-y select-none"
          onPointerDown={(e) => { down.current = e.clientX; }}
          onPointerCancel={() => { down.current = null; }}
          onDragStart={(e) => e.preventDefault()} // native image drag would swallow the swipe
          onPointerUp={(e) => {
            const x0 = down.current;
            down.current = null;
            if (x0 === null) return;
            const dx = e.clientX - x0;
            if (Math.abs(dx) > 24) return go(dx < 0 ? 1 : -1);
            const slot = Number((e.target as HTMLElement).closest<HTMLElement>("[data-slot]")?.dataset.slot);
            if (Math.abs(slot) === 1) go(slot);
          }}
        >
          {/* keyed by absolute position, so a card keeps its node (and slides) as `c` moves */}
          {[-2, -1, 0, 1, 2].map((o) => <ModelCard key={c + o} mod={MODELS[(((c + o) % 3) + 3) % 3]} slot={o} moving={moving} />)}
        </div>
        <div className="flex h-[0.49rem] items-center gap-[0.296rem]">
          {[33, 21, 22].map((w, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Show ${MODELS[(i + 1) % 3].t}`}
              onClick={() => go(((i - pg + 4) % 3) - 1)}
              className={cn("relative block h-[0.375rem] cursor-pointer rounded-[2.17rem] border-0 p-0 qe-sl-dot before:absolute before:inset-[-1rem_-0.25rem] before:content-['']", i === pg ? "bg-[#2d2d2d]" : "bg-[#a8a8a8]")}
              style={{ width: r(i === pg ? 33 : w === 33 ? 21 : w) }}
            />
          ))}
        </div>
      </div>
      <Grain opacity={0.5} />
    </div>
  );
}
